import { useCallback, useEffect, useMemo, useState } from 'react';

import { adminContent } from 'services/api';
import type { Course, CourseEligibility, Flow } from 'types';
import ContentAssignmentEditor, {
  type DraftCourseAssignment,
  type DraftFlowAssignment,
} from 'components/admin/ContentAssignmentEditor';
import { LoadingAnimation } from 'components/ui/loading-animation';
import { useToast } from 'components/ui/use-toast';
import type { AdminUserListItem } from 'pages/Admin/UserManagement/types';

interface CourseAssignment {
  id: number;
  course: Course;
  order: number;
  assigned_at: string;
}

interface FlowAssignment {
  id: number;
  flow: Flow;
  assigned_at: string;
}

interface AssignmentsData {
  course_assignments: CourseAssignment[];
  flow_assignments: FlowAssignment[];
  eligibility_by_course_id?: Record<number, CourseEligibility>;
}

/** Lightweight item for the local draggable lists */
type ContentAssignmentProps = {
  selectedUser: AdminUserListItem | null;
};

const ContentAssignment: React.FC<ContentAssignmentProps> = ({ selectedUser }) => {
  const { addToast } = useToast();

  // Server assignments (source of truth from backend)
  const [assignments, setAssignments] = useState<AssignmentsData | null>(null);
  const [loadingAssignments, setLoadingAssignments] = useState(false);

  // Local draft state (editable, saved on demand)
  const [draftCourses, setDraftCourses] = useState<DraftCourseAssignment[]>([]);
  const [draftFlows, setDraftFlows] = useState<DraftFlowAssignment[]>([]);
  const [savingCourses, setSavingCourses] = useState(false);
  const [savingFlows, setSavingFlows] = useState(false);

  // Load assignments for selected user. Use silent=true to skip the full-page spinner (e.g. after saving).
  const loadAssignments = useCallback(async (userId: number, silent = false) => {
    if (!silent) setLoadingAssignments(true);
    try {
      const res = await adminContent.userTools.getAssignments(String(userId));
      const data = res.data as AssignmentsData;
      setAssignments(data);
      // Initialize drafts from server state
      setDraftCourses(data.course_assignments.map((a) => ({ course_id: a.course.id, name: a.course.name })));
      setDraftFlows(data.flow_assignments.map((a) => ({
        flow_id: a.flow.id,
        name: a.flow.title || a.flow.name,
      })));
    } catch {
      setAssignments(null);
      setDraftCourses([]);
      setDraftFlows([]);
    } finally {
      setLoadingAssignments(false);
    }
  }, []);

  useEffect(() => {
    if (selectedUser) {
      loadAssignments(selectedUser.id);
    } else {
      setAssignments(null);
      setDraftCourses([]);
      setDraftFlows([]);
    }
  }, [selectedUser, loadAssignments]);

  // Dirty tracking
  const coursesDirty = useMemo(() => {
    if (!assignments) return false;
    const serverIds = assignments.course_assignments.map((a) => a.course.id);
    const draftIds = draftCourses.map((c) => c.course_id);
    return JSON.stringify(serverIds) !== JSON.stringify(draftIds);
  }, [assignments, draftCourses]);

  const flowsDirty = useMemo(() => {
    if (!assignments) return false;
    const serverIds = assignments.flow_assignments.map((a) => a.flow.id);
    const draftIds = draftFlows.map((f) => f.flow_id);
    if (serverIds.length !== draftIds.length) return true;
    const draftSet = new Set(draftIds);
    return serverIds.some((id) => !draftSet.has(id));
  }, [assignments, draftFlows]);

  const getUserDisplayName = (u: AdminUserListItem) => {
    const name = [u.first_name, u.last_name].filter(Boolean).join(' ');
    return name || u.email;
  };

  const handleSaveCourses = async () => {
    if (!selectedUser) return;
    setSavingCourses(true);
    try {
      await adminContent.userTools.setCourses({
        user_id: selectedUser.id,
        course_ids: draftCourses.map((c) => c.course_id),
      });
      // Preserve unsaved flow drafts across the reload
      const pendingFlows = draftFlows;
      await loadAssignments(selectedUser.id, true);
      setDraftFlows(pendingFlows);
      addToast({ title: 'Course assignments saved' });
    } catch {
      addToast({ title: 'Failed to save course assignments', variant: 'destructive' });
    } finally {
      setSavingCourses(false);
    }
  };

  const handleSaveFlows = async () => {
    if (!selectedUser) return;
    setSavingFlows(true);
    try {
      await adminContent.userTools.setFlows({
        user_id: selectedUser.id,
        flow_ids: draftFlows.map((f) => f.flow_id),
      });
      // Preserve unsaved course drafts across the reload
      const pendingCourses = draftCourses;
      await loadAssignments(selectedUser.id, true);
      setDraftCourses(pendingCourses);
      addToast({ title: 'Module assignments saved' });
    } catch {
      addToast({ title: 'Failed to save module assignments', variant: 'destructive' });
    } finally {
      setSavingFlows(false);
    }
  };

  if (!selectedUser) {
    return (
      <div className="flex min-h-[240px] items-center justify-center text-sm text-muted-foreground">
        Select a user above to manage their content assignments.
      </div>
    );
  }

  if (loadingAssignments) {
    return (
      <div className="py-12 flex justify-center">
        <LoadingAnimation />
      </div>
    );
  }

  if (!assignments) return null;

  return (
    <ContentAssignmentEditor
      courseTitle={`Manual Course Assignments for ${getUserDisplayName(selectedUser)}`}
      flowTitle="Manual Standalone Module Assignments"
      flowDescription="Manual modules assigned here are added on top of inherited organization and cohort defaults. System modules (Daily Checkin, Intake, etc.) are always accessible."
      courseEmptyMessage="No manual courses assigned. Use the dropdown below to add courses."
      flowEmptyMessage="No manual standalone modules assigned. Use the dropdown below to add modules."
      courses={draftCourses}
      eligibilityByCourseId={assignments.eligibility_by_course_id}
      flows={draftFlows}
      onCoursesChange={setDraftCourses}
      onFlowsChange={setDraftFlows}
      onSaveCourses={handleSaveCourses}
      onSaveFlows={handleSaveFlows}
      coursesDirty={coursesDirty}
      flowsDirty={flowsDirty}
      savingCourses={savingCourses}
      savingFlows={savingFlows}
    />
  );
};

export default ContentAssignment;
